import React, { useEffect, useState } from 'react';
import usePatternMemory from '../../hooks/usePatternMemory';
import { fetchTodayCheckin, fetchHormoneData } from '../../lib/userDataService';

/**
 * ReportsScreen — premium editorial summary of the user's data.
 *
 * Sections:
 *   - Cycle Summary          (current phase, day, cycle length)
 *   - Symptom Summary        (top symptoms last 30 days)
 *   - Hormone Summary        (latest lab values if uploaded)
 *   - Recovery Summary       (7-day + 30-day averages, trend)
 *   - Pattern Detection      (from usePatternMemory observations)
 *   - Theo's Observations    (headline pattern notes with confidence)
 *   - Doctor Discussion Notes(actionable bullet list, exportable-ready)
 *
 * Export architecture: report content lives inside <section
 * data-report-id="…"> containers so a future PDF exporter can walk the
 * DOM and print each section as a page. The "Export" button in the
 * hero is scaffolded here (disabled until PDF wiring lands).
 */

const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

export default function ReportsScreen({ userId, phase, dayNumber, cycleLength, waiting }) {
  const [checkin, setCheckin] = useState(null);
  const [hormones, setHormones] = useState(null);
  const [loading, setLoading] = useState(true);
  const { symptomHistory, recoverySeries, observations, loading: pmLoading } = usePatternMemory(userId);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) { if (alive) setLoading(false); return; }
      const [c, h] = await Promise.all([fetchTodayCheckin(userId), fetchHormoneData(userId)]);
      if (!alive) return;
      setCheckin(c); setHormones(h); setLoading(false);
    })();
    return () => { alive = false; };
  }, [userId]);

  const busy = loading || pmLoading;

  // Top symptoms last 30 days
  const topSymptoms = React.useMemo(() => {
    const buckets = {};
    ['acne', 'hair_loss', 'mood', 'fatigue', 'sleep_quality', 'weight_change'].forEach(k => {
      const total = symptomHistory.reduce((s, r) => s + (r[k] || 0), 0);
      if (total > 0) buckets[k] = total;
    });
    return Object.entries(buckets)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([k, v]) => ({ key: k, total: v, label: PRETTY_SYMPTOM[k] || k }));
  }, [symptomHistory]);

  const recovery7  = avg(recoverySeries.slice(-7).map(r => r.score));
  const recovery30 = avg(recoverySeries.slice(-30).map(r => r.score));
  const recoveryTrend = recovery7 - recovery30;

  return (
    <div className="cx-sub-body" data-testid="reports-screen">
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">REPORT · {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
        <h1 className="cx-intel-h1">Your health, told back to you.</h1>
        <div className="cx-intel-conf-chip" style={{ marginTop: 16 }}>
          <span className="cx-conf-dot" />
          {busy ? 'Compiling' : `${observations.length} observation${observations.length === 1 ? '' : 's'} detected`}
        </div>
        <button className="cx-report-export" data-testid="report-export-btn" disabled title="PDF export coming soon">
          Export as PDF
        </button>
      </section>

      <section className="cx-intel-section" data-report-id="cycle-summary">
        <div className="cx-intel-label">Cycle summary</div>
        <div className="cx-report-grid">
          <ReportKV k="Current phase" v={phase} />
          <ReportKV k="Day of cycle" v={String(dayNumber)} />
          <ReportKV k="Cycle length" v={`${cycleLength} days`} />
          <ReportKV k="Status" v={waiting ? 'Awaiting period' : 'Active cycle'} />
        </div>
      </section>

      <section className="cx-intel-section" data-report-id="symptom-summary">
        <div className="cx-intel-label">Symptom summary · last 30 days</div>
        {topSymptoms.length ? (
          <div className="cx-report-list">
            {topSymptoms.map((s) => (
              <div key={s.key} className="cx-report-row" data-testid={`report-sym-${s.key}`}>
                <span className="cx-report-row-k">{s.label}</span>
                <span className="cx-report-row-v">total load {s.total}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="cx-fine-print">No symptoms logged in the last 30 days.</p>
        )}
      </section>

      <section className="cx-intel-section" data-report-id="hormone-summary">
        <div className="cx-intel-label">Hormone summary</div>
        {hormones ? (
          <div className="cx-report-list">
            {['lh', 'fsh', 'testosterone', 'amh', 'estradiol', 'progesterone'].map((k) => (
              hormones[k] != null && (
                <div key={k} className="cx-report-row">
                  <span className="cx-report-row-k">{k.toUpperCase()}</span>
                  <span className="cx-report-row-v">{hormones[k]}</span>
                </div>
              )
            ))}
          </div>
        ) : (
          <p className="cx-fine-print">No hormone reports uploaded yet. Health tab → upload to enrich this section.</p>
        )}
      </section>

      <section className="cx-intel-section" data-report-id="recovery-summary">
        <div className="cx-intel-label">Recovery summary</div>
        <div className="cx-report-grid">
          <ReportKV k="7-day average"  v={`${Math.round(recovery7)}%`} />
          <ReportKV k="30-day average" v={`${Math.round(recovery30)}%`} />
          <ReportKV k="Trend" v={`${recoveryTrend >= 0 ? '↑' : '↓'} ${Math.abs(Math.round(recoveryTrend))}pt`} />
          <ReportKV k="Days logged" v={String(recoverySeries.length)} />
        </div>
      </section>

      <section className="cx-intel-section" data-report-id="pattern-detection">
        <div className="cx-intel-label">Pattern detection · Theo</div>
        {observations.length ? (
          <div className="cx-report-list">
            {observations.map((o) => (
              <div key={o.key} className={`cx-report-obs cx-report-obs--${o.tone || 'brand'}`} data-testid={`report-obs-${o.key}`}>
                <div className="cx-report-obs-title">{o.headline}</div>
                <div className="cx-report-obs-evidence">{o.evidence}</div>
                <div className="cx-report-obs-confidence">Confidence · {o.confidence}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="cx-fine-print">Not enough historical data yet — keep logging to reveal patterns.</p>
        )}
      </section>

      <section className="cx-intel-section" data-report-id="doctor-notes">
        <div className="cx-intel-label">Doctor discussion notes</div>
        <ul className="cx-doc-list">
          <li>Cycle length averaging <strong>{cycleLength} days</strong> · currently on day <strong>{dayNumber}</strong>{waiting ? ' (awaiting period).' : '.'}</li>
          {topSymptoms.length > 0 && (
            <li>Top recurring symptom{topSymptoms.length > 1 ? 's' : ''}: <strong>{topSymptoms.map(s => s.label).join(', ')}</strong>.</li>
          )}
          {hormones?.lh && hormones?.fsh && (
            <li>Most recent LH/FSH ratio: <strong>{(hormones.lh / hormones.fsh).toFixed(2)}</strong>.</li>
          )}
          {observations.slice(0, 2).map((o) => (
            <li key={o.key}>{o.evidence}</li>
          ))}
        </ul>
        <p className="cx-fine-print" style={{ marginTop: 12 }}>
          These notes are generated from your logs to help structure a clinical conversation.
        </p>
      </section>
    </div>
  );
}

function ReportKV({ k, v }) {
  return (
    <div className="cx-report-kv">
      <span className="cx-report-kv-k">{k}</span>
      <span className="cx-report-kv-v">{v}</span>
    </div>
  );
}

const PRETTY_SYMPTOM = {
  acne: 'Acne', hair_loss: 'Hair loss', mood: 'Mood shifts', fatigue: 'Fatigue',
  sleep_quality: 'Sleep quality', weight_change: 'Weight change',
};
